import React, { useState } from 'react';
import { MessageSquare, Send, Shield, User } from 'lucide-react';
import { fmtDateTime } from '../../data/onboardingPlan';

// A compact comment thread shared by tasks and work logs. `canComment` decides
// whether the composer is shown, so the engineer's read-only views reuse it.
export default function CommentThread({ comments = [], canComment, onAdd, placeholder = 'Add a comment…' }) {
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const text = body.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      await onAdd(text);
      setBody('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      {comments.length > 0 && (
        <ul className="space-y-2">
          {comments.map((c) => {
            const isReviewer = c.authorRole === 'admin' || c.authorRole === 'mentor';
            return (
              <li key={c.id} className="flex gap-2.5">
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: isReviewer ? 'rgba(249,101,22,0.12)' : 'rgba(100,116,139,0.12)' }}
                  title={isReviewer ? 'Reviewer' : 'Engineer'}
                >
                  {isReviewer ? <Shield size={13} style={{ color: 'var(--accent)' }} /> : <User size={13} className="text-slate-500" />}
                </span>
                <div className="min-w-0 flex-1 bg-slate-50 border border-slate-150 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-slate-800">{c.authorName || c.authorEmail}</span>
                    {isReviewer && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ color: 'var(--accent)', background: 'rgba(249,101,22,0.10)' }}>
                        {c.authorRole === 'admin' ? 'Admin' : 'Mentor'}
                      </span>
                    )}
                    <span className="text-[10px] text-slate-400 font-mono">{fmtDateTime(c.createdAt)}</span>
                  </div>
                  <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap break-words">{c.body}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {canComment ? (
        <div className="flex items-start gap-2">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            placeholder={placeholder}
            className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition resize-none"
          />
          <button
            type="button"
            onClick={submit}
            disabled={!body.trim() || saving}
            className="px-3 py-2 rounded-lg text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white transition disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            title="Post comment"
          >
            <Send size={15} />
          </button>
        </div>
      ) : comments.length === 0 ? (
        <p className="text-xs text-slate-400 italic flex items-center gap-1.5">
          <MessageSquare size={12} /> No comments yet
        </p>
      ) : null}
    </div>
  );
}
